import { z } from "zod";

/**
 * The comment layer (docs/02 §3.2 "add a note", designed in docs/07).
 *
 * Mirrors the Postgres enums in @pinpoint/db one-for-one, on the same terms as
 * pipeline.ts: the database owns storage, this file owns the wire format, and
 * the compiler catches drift because the Worker assigns values from here into
 * rows typed by there.
 */

export const commentTargetSchema = z.enum([
  "company",
  "contact",
  "sequence",
  "run",
]);
export type CommentTarget = z.infer<typeof commentTargetSchema>;

/**
 * Upper bound on a comment body.
 *
 * Generous enough for a real handover note, small enough that a runaway agent
 * cannot write a novel into a row that renders in a timeline.
 */
export const COMMENT_MAX_LENGTH = 8000;

export const commentBodySchema = z
  .string()
  .trim()
  .min(1, "A comment needs some text.")
  .max(
    COMMENT_MAX_LENGTH,
    `Comments are limited to ${COMMENT_MAX_LENGTH} characters.`,
  );

export const createCommentSchema = z.object({
  entityType: commentTargetSchema,
  entityId: z.string().uuid(),
  bodyMd: commentBodySchema,
  /** Replying to a comment. Threads are one level: a reply to a reply
   *  attaches to the same root. */
  parentId: z.string().uuid().nullish(),
});
export type CreateCommentInput = z.infer<typeof createCommentSchema>;

export const updateCommentSchema = z.object({
  bodyMd: commentBodySchema,
});

/** One comment as the UI receives it. */
export const commentSchema = z.object({
  id: z.string().uuid(),
  entityType: commentTargetSchema,
  entityId: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  bodyMd: z.string(),

  authorType: z.enum(["user", "agent", "system"]),
  authorLabel: z.string(),
  authorUserId: z.string().uuid().nullable(),
  /** Agent comments link back to the run that prompted them. */
  runId: z.string().uuid().nullable(),

  resolvedAt: z.string().nullable(),
  resolvedByLabel: z.string().nullable(),
  editedAt: z.string().nullable(),
  createdAt: z.string(),

  /** Users named with @ in the body, resolved at write time. */
  mentions: z.array(
    z.object({ userId: z.string().uuid(), label: z.string() }),
  ),

  /** Whether the *calling* user may edit or delete this one. */
  canEdit: z.boolean(),
});
export type CommentDto = z.infer<typeof commentSchema>;

export const commentListSchema = z.object({
  comments: z.array(commentSchema),
  /** Unresolved count for the target, so a tab can carry a badge. */
  openCount: z.number().int(),
});
export type CommentList = z.infer<typeof commentListSchema>;
